import React, { useEffect, useState, useRef } from "react";
import { Button, Col, Container, Row } from "reactstrap";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import MainNavigation from "../components/MainNavigation";
import {
  addOrderAction,
  getPaymentMethodAction,
} from "../store/actions/orderAction";
import {
  addCartDetails,
  clearCartAction,
  clearCartDetails,
  updateTableData,
} from "../store/actions/cartAction";
import { getRestaurantsAction } from "../store/actions/restaurantsAction";
import PaymentMethodCard from "../components/Payment/PaymentMethodCard";
import CardPaymentSection from "../components/Payment/CardPaymentSection";
import SecurityInfo from "../components/Payment/SecurityInfo";
import HolidayNotice from "../components/RestaurantMenu/HolidayNotice";
import { usePaymentSDKValidation } from "../components/Payment/usePaymentSDKValidation";

const Payment = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const isMountedRef = useRef(true);

  const { darkMode, color } = useSelector((state) => state.global);
  const { loader } = useSelector((state) => state.orders);
  const { uid } = useSelector((state) => state.authUser);
  const { singleUser } = useSelector((state) => state.user);
  const { cartDetails, cartItems } = useSelector((state) => state.cart);
  const { restaurantsDetail } = useSelector((state) => state.restaurants);
  const [isHoliday, setIsHoliday] = useState({
    isHoliday: false,
    title: "",
    storeTiming: false,
    type: "",
  });
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);

  const { paypalSdkValid, stripeSdkValid } =
    usePaymentSDKValidation(restaurantsDetail);

  const paymentOptions = [
    {
      methodName: "cash",
      displayName: "Pay With Cash",
    },
  ];

  if (
    (paypalSdkValid &&
      restaurantsDetail?.paymentConnections?.paypal &&
      restaurantsDetail?.paypalClientKey) ||
    (stripeSdkValid &&
      restaurantsDetail?.paymentConnections?.stripe &&
      restaurantsDetail?.stripeClientKey)
  ) {
    paymentOptions.push({
      methodName: "Stripe",
      displayName: "Pay With Card",
    });
  }

  const [paymentMethod, setPaymentMethod] = useState("");
  const [payment, setPayment] = useState();

  const restaurantID = cartDetails?.restaurantId;
  const paypalFees = restaurantsDetail?.forwardFees?.find(
    (val) => val?.provider === "paypal"
  );
  const stripeFees = restaurantsDetail?.forwardFees?.find(
    (val) => val?.provider === "stripe"
  );

  const isToday = (dateString) => {
    const givenDate = new Date(dateString);
    const today = new Date();
    return (
      givenDate.getFullYear() === today.getFullYear() &&
      givenDate.getMonth() === today.getMonth() &&
      givenDate.getDate() === today.getDate()
    );
  };

  const isHolidayToday = restaurantsDetail?.holidays?.some((holiday) =>
    isToday(holiday.date)
  );

  const addOrderData = (e) => {
    e.preventDefault();
    if (!paymentMethod) return toast.info("Select the payment method");

    setIsProcessingOrder(true);

    if (isHoliday?.isHoliday || isHoliday?.storeTiming) {
      const typeLabel =
        isHoliday?.type === "dinein"
          ? "Dine-in"
          : isHoliday?.type === "delivery"
          ? "Delivery"
          : "Takeaway";

      const typeIcon =
        isHoliday?.type === "dinein"
          ? "🍽️"
          : isHoliday?.type === "delivery"
          ? "🚚"
          : "🛍️";

      toast.warning(
        isHoliday?.isHoliday
          ? `📅 Attention: The ${restaurantsDetail?.name} store is closed today due to ${isHoliday?.title}. Please check back tomorrow.`
          : `${typeIcon} Notice: ${typeLabel} service is currently unavailable as operating hours have ended. Please select a different option or check back tomorrow.`,
        {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          style: {
            backgroundColor: "#fff3cd",
            color: "#856404",
            fontWeight: "500",
            fontSize: "15px",
          },
        }
      );

      if (isHoliday?.storeTiming) {
        history.push("/order-options");
        dispatch(clearCartAction());
      } else if (isHoliday?.isHoliday) {
        history.push("/restaurants");
        dispatch(clearCartAction());
        dispatch(clearCartDetails());
      }
      setIsProcessingOrder(false);
      return;
    }

    const { restaurantId, menuId, ...filteredCartDetails } = cartDetails;
    const obj = {
      ...filteredCartDetails,
      customerID: uid,
      customerName: singleUser?.name,
      paymentMethod,
      status: "pending",
      paymentRecieved: cartDetails?.orderType !== "delivery" ? true : false,
    };
    dispatch(
      addOrderAction(obj, (orderID) => {
        dispatch(addCartDetails(obj));
        setTimeout(() => {
          dispatch(clearCartAction());
          history.push(`/tracking/orderID=${orderID}`);
          if (isMountedRef.current) {
            setIsProcessingOrder(false);
          }
        }, 200);
        localStorage.removeItem("cartPath");
      })
    );

    if (obj.tableNumber) {
      dispatch(
        updateTableData(
          obj.restaurantID,
          uid,
          obj.tableNumber,
          obj.timer,
          restaurantsDetail?.name
        )
      );
    }
  };

  const getPaypalDetail = (details, finalAmount) => {
    setIsProcessingOrder(true);

    const obj = {
      ...cartDetails,
      totalAmount: finalAmount || 0,
      customerID: uid,
      customerName: singleUser?.name,
      paymentMethod: "Paypal",
      paymentRecieved: true,
      status: "pending",
      paymentID: details?.id,
    };

    dispatch(
      addOrderAction(obj, (orderID) => {
        dispatch(addCartDetails(obj));
        history.push(`/tracking/orderID=${orderID}`);
        dispatch(clearCartAction());
        localStorage.removeItem("cartPath");
        if (isMountedRef.current) {
          setIsProcessingOrder(false);
        }
      })
    );
  };

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (restaurantID) {
      dispatch(getRestaurantsAction(restaurantID));
    }
    dispatch(getPaymentMethodAction(uid));
    window.scrollTo(0, 0);
    if (isHolidayToday && !isProcessingOrder) {
      history.push("/restaurants");
      dispatch(clearCartAction());
      dispatch(clearCartDetails());
    }
  }, [dispatch, restaurantID, uid, history, isHolidayToday, isProcessingOrder]);

  useEffect(() => {
    if (isProcessingOrder) return;

    if (!cartDetails?.restaurantId) {
      dispatch(clearCartAction());
      dispatch(clearCartDetails());
      history.push("/restaurants");
      return;
    }
    if (!cartDetails?.order || cartItems?.length === 0) {
      history.push(`/restaurant-details/${cartDetails?.menuId}`);
      toast.warning("Kindly add items to your cart to proceed.");
      return;
    }

    if (
      cartDetails?.orderType === "delivery" &&
      !cartDetails?.deliveryAddress
    ) {
      toast.warning("Please add a delivery address.");
      history.push("/cart");
      return;
    }

    if (cartDetails?.orderType === "dinein" && !cartDetails?.tableNumber) {
      toast.warning("Please select a table for dine-in.");
      history.push("/cart");
      return;
    }
  }, [cartDetails, dispatch, history, isProcessingOrder]);

  return (
    <div className="cart">
      <Container>
        <HolidayNotice
          restaurant={restaurantsDetail}
          setIsHoliday={setIsHoliday}
        />
        <MainNavigation title={"Payment"} />
        <Row className="d-flex flex-column pt-3">
          <Col>
            <h4
              className={
                darkMode
                  ? "fs--18 fw--600 mb-0 normal-heading-dark"
                  : "fs--18 fw--600 mb-0 normal-heading"
              }
            >
              Choose your Payment Method
            </h4>
          </Col>
          <Col className="my-4 pb-3 d-flex flex-column align-items-md-center justify-content-start">
            {paymentOptions?.map((option, idx) => (
              <PaymentMethodCard
                key={idx}
                darkMode={darkMode}
                color={color}
                methodName={option.methodName}
                displayName={option.displayName}
                disabled={isHolidayToday || isProcessingOrder}
                paymentMethod={paymentMethod}
                setPaymentMethod={(method) => {
                  setPaymentMethod(method);
                }}
              />
            ))}

            {paymentMethod != "cash" &&
              paymentMethod != "" &&
              restaurantsDetail && (
                <CardPaymentSection
                  darkMode={darkMode}
                  restaurantsDetail={restaurantsDetail}
                  location={cartDetails}
                  paypalFees={paypalFees}
                  stripeFees={stripeFees}
                  paypalSdkValid={paypalSdkValid}
                  stripeSdkValid={stripeSdkValid}
                  getPaypalDetail={getPaypalDetail}
                  setPayment={setPayment}
                />
              )}

            <SecurityInfo darkMode={darkMode} />
          </Col>
        </Row>

        {paymentMethod == "cash" && (
          <Row className="d-flex justify-content-center p-3">
            <Button
              style={{ background: color }}
              className="top_cards__addtocart fs--16 fw--400 my-4"
              onClick={addOrderData}
              disabled={loader || paymentMethod !== "cash" || isProcessingOrder}
            >
              Confirm
            </Button>
          </Row>
        )}
      </Container>
    </div>
  );
};

export default Payment;
